import React, { useEffect, useRef, useState } from "react";
import { ResponsiveContainer } from "recharts";

/**
 * Drop-in wrapper for Recharts' <ResponsiveContainer> that defers mounting
 * the chart until the parent has a non-zero width. Avoids the
 *   "The width(-1) and height(-1) of chart should be greater than 0…"
 * warning storm that React's dev overlay flashes briefly whenever a tab is
 * switched or a chart re-mounts inside a flex/grid layout that hasn't laid
 * out yet.
 *
 * Use exactly the same children you'd pass to <ResponsiveContainer>.
 */
export default function ChartContainer({ height = "100%", width = "100%", children, ...rest }) {
  const wrapperRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return undefined;
    const update = () => {
      // ≥1px is enough — once we're above 0, Recharts can take it from there.
      if (el.clientWidth > 0 && el.clientHeight > 0) setReady(true);
    };
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={wrapperRef} style={{ width, height, minWidth: 0 }}>
      {ready && (
        <ResponsiveContainer width="100%" height="100%" debounce={1} {...rest}>
          {children}
        </ResponsiveContainer>
      )}
    </div>
  );
}
